import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, fromEvent, merge, Subscription } from 'rxjs';
import { throttleTime, takeUntil } from 'rxjs/operators';
import { LogOut, SetValidationError } from 'src/app/features/admin/stores/admin.action';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { DialogType, IAppState } from 'src/app/shared/shared.interface';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class IdleService implements OnDestroy {
  private readonly IDLE_TIMEOUT_MS = (environment.session?.idleTimeoutSeconds ?? 900) * 1000; // Convert seconds to milliseconds, default 15 min
  private readonly WARNING_TIMEOUT_MS = (environment.session?.warningTimeoutSeconds ?? 60) * 1000; // Warning shown before timeout, default 60 sec

  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private iframeCheckTimer: ReturnType<typeof setInterval> | null = null;
  private destroy$ = new Subject<void>();
  private activitySubscription: Subscription | null = null;
  private isRunning = false;
  private isWarningShown = false;
  private countdownSeconds = 0;
  private lastActivityTime = Date.now();

  constructor(
    private router: Router,
    private store: Store<IAppState>,
    private ngZone: NgZone,
    private dialogService: DialogService
  ) {}

  /**
   * Start monitoring user activity for idle detection
   */
  startWatching(): void {
    console.log('[IdleService] startWatching() called, isRunning:', this.isRunning);
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    console.log('[IdleService] Started. IDLE_TIMEOUT_MS:', this.IDLE_TIMEOUT_MS, 'WARNING_TIMEOUT_MS:', this.WARNING_TIMEOUT_MS);

    // Run outside Angular zone to avoid triggering change detection on every event
    this.ngZone.runOutsideAngular(() => {
      const activityEvents$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll', { passive: true } as AddEventListenerOptions),
        fromEvent(document, 'touchstart', { passive: true } as AddEventListenerOptions)
      );

      this.activitySubscription = activityEvents$
        .pipe(
          throttleTime(1000), // Throttle to avoid excessive timer resets
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.resetTimer();
        });

      // Detect user activity inside iframes (e.g., Power BI reports)
      // Events inside iframes don't bubble to the parent document,
      // so we periodically check if an iframe has focus
      this.iframeCheckTimer = setInterval(() => {
        const activeEl = document.activeElement;
        if (activeEl && activeEl.tagName === 'IFRAME') {
          console.log('[IdleService] iframe detected as active element, resetting timer');
          this.resetTimer();
        }
      }, 2000);

      // Start the initial timer
      this.resetTimer();
    });
  }

  /**
   * Stop monitoring user activity
   */
  stopWatching(): void {
    this.isRunning = false;
    this.clearAllTimers();

    if (this.iframeCheckTimer) {
      clearInterval(this.iframeCheckTimer);
      this.iframeCheckTimer = null;
    }

    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
  }

  /**
   * Reset the idle timer
   */
  resetTimer(): void {
    // Do nothing if idle detection is not active
    if (!this.isRunning) {
      return;
    }

    // If warning is shown, ignore activity events - only button click should continue session
    if (this.isWarningShown) {
      return;
    }

    this.clearAllTimers();
    this.lastActivityTime = Date.now();
    console.log('[IdleService] Timer reset. Warning will show in', Math.max(this.IDLE_TIMEOUT_MS - this.WARNING_TIMEOUT_MS, 0) / 1000, 'seconds'); // Track when user was last active

    // Calculate when to show warning (idle timeout - warning time)
    const warningDelayMs = Math.max(this.IDLE_TIMEOUT_MS - this.WARNING_TIMEOUT_MS, 0);

    // Set timer to show warning before timeout
    this.warningTimer = setTimeout(() => {
      this.ngZone.run(() => {
        // Check if we should still show warning or go directly to logout
        // This handles the case where user returns after idle time has expired
        const timeSinceLastActivity = Date.now() - this.lastActivityTime;
        if (timeSinceLastActivity >= this.IDLE_TIMEOUT_MS) {
          // Idle time already expired, go directly to logout
          this.onIdle();
        } else {
          // Still have time, show warning
          this.showWarningPopup();
        }
      });
    }, warningDelayMs);
  }

  /**
   * Clear all timers
   */
  private clearAllTimers(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  /**
   * Show warning popup with countdown
   */
  private showWarningPopup(): void {
    console.log('[IdleService] Showing warning popup');
    this.isWarningShown = true;
    this.countdownSeconds = Math.floor(this.WARNING_TIMEOUT_MS / 1000);

    // Start countdown timer
    this.countdownTimer = setInterval(() => {
      this.ngZone.run(() => {
        this.countdownSeconds--;
        if (this.countdownSeconds <= 0) {
          this.onIdle();
        } else {
          // Update dialog message with new countdown
          this.updateWarningDialog();
        }
      });
    }, 1000);

    // Show initial dialog
    this.updateWarningDialog();

    // Set final timeout for auto-logout
    this.idleTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.onIdle();
      });
    }, this.WARNING_TIMEOUT_MS);
  }

  /**
   * Update warning dialog with current countdown
   */
  private updateWarningDialog(): void {
    this.dialogService.display(
      DialogType.Warning,
      `Your session is about to expire due to inactivity. You will be logged out in ${this.countdownSeconds} seconds. Do you want to continue?`,
      [
        {
          value: 'Continue Session',
          color: 'primary',
          click: () => this.continueSession(),
        },
        {
          value: 'Logout',
          color: 'secondary',
          click: () => this.onIdle(),
        },
      ]
    );
  }

  /**
   * Continue the session - reset timers and hide warning
   */
  private continueSession(): void {
    this.clearAllTimers();
    this.isWarningShown = false;
    this.resetTimer();
  }

  /**
   * Handle idle timeout - logout user and redirect to login
   */
  private onIdle(): void {
    console.log('[IdleService] onIdle() - logging out user');
    this.isWarningShown = false;
    this.clearAllTimers();
    console.log(`User idle for ${environment.session?.idleTimeoutSeconds ?? 900} seconds. Logging out...`);

    // Close any existing dialog before logout
    this.dialogService.closeAll();

    // Show logout confirmation dialog briefly
    this.dialogService.display(
      DialogType.Info,
      'You have been logged out due to inactivity.',
      [
        {
          value: 'OK',
          color: 'primary',
          click: () => {
            // Proceed with logout after user acknowledges
            this.performLogout();
          },
        },
      ]
    );

    // Auto-proceed with logout after 3 seconds if user doesn't click OK
    setTimeout(() => {
      this.performLogout();
    }, 3000);
  }

  /**
   * Perform the actual logout operations
   */
  private performLogout(): void {
    // Close all dialogs
    this.dialogService.closeAll();

    // Dispatch logout action to call backend logout endpoint
    this.store.dispatch(new SetValidationError(''));
    this.store.dispatch(new LogOut());

    // Clear all stored tokens and session data
    localStorage.clear();
    sessionStorage.clear();

    // Clear browser caches if available
    if (window.caches) {
      caches.keys().then((keyList) => {
        return Promise.all(
          keyList.map((key) => {
            return caches.delete(key);
          })
        );
      });
    }

    // Stop watching for activity
    this.stopWatching();

    // Redirect to login page
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopWatching();
  }
}
